import { Button, IconButton, MenuItem, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useEffect, useMemo, useState } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import React from "react";
import { useLazyGetStoreListQuery, useDeleteStoreMutation, useUpdateStoreMutation } from "../../services/store";
import DeleteConfirmationModal from "../../components/DeleteModal";
import Pagination from "../../components/Panigation";
import { isValidInput } from "../../utils/validations";
import usePermission from "../../hooks/userAccess";

const ManageStores = () => {
  const navigate = useNavigate();
  const { isAdd, isEdit, isDelete, isView } = usePermission({
    label: 'Store Management',
    page_permission: 'isView',
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [Status, setStatus] = React.useState('0');

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatus(event.target.value as string);
  };

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openDelete, setOpenDelete] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [triggerList, { data: listRes, isFetching }] = useLazyGetStoreListQuery();
  const [deleteStore, { isLoading: isDeleting }] = useDeleteStoreMutation();
  const [updateStore, { isLoading: isUpdating }] = useUpdateStoreMutation();

  useEffect(() => {
    triggerList({ page, limit, search: debouncedSearchTerm, status: Status });
  }, [triggerList, page, limit, debouncedSearchTerm, Status]);

  type RowType = { id: string; name: string; code: string; status: string; address: string; region: string; date: string; raw: any };

  const rows: RowType[] = useMemo(() => {
    const data = (listRes as any)?.data?.store;
    const list = Array.isArray(data) ? data : (data?.list || []);
    setTotalCount((data?.total as number) || list.length || 0);
    return list.map((item: any) => ({
      id: item?._id || item?.id,
      name: item?.name,
      code: item?.storeCode,
      status: item?.status === 1 ? "Open" : "Closed",
      address: item?.address,
      region: item?.city,
      date: item?.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-",
      raw: item,
    }));
  }, [listRes]);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const toggleDeleteModal = () => setOpenDelete(!openDelete);

  const handleDelete = async () => {
    try {
      await deleteStore({ id: activeId }).unwrap();
      toggleDeleteModal();
      triggerList({ page, limit, search: debouncedSearchTerm });
    } catch (e) {
      console.log(e);
    }
  };

  const handleToggle = async (row: RowType, nextChecked: boolean) => {
    const id = row?.id;
    if (!id) return;
    try {
      await updateStore({ body: { isActive: nextChecked }, id }).unwrap();
      triggerList({ page, limit, search: debouncedSearchTerm });
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>Store Management</h1>
        <p>
          <button onClick={() => navigate("/dashboard", { replace: true })}>Dashboard</button> - Store Management
        </p>
      </div>
      {isView && <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              placeholder="Search..."
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              value={searchTerm}
              onCross={() => setSearchTerm('')}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
            <div className=" form">
              <Select
                fullWidth
                value={Status}
                onChange={handleStatusChange} >
                <MenuItem disabled value="default">Select Status</MenuItem>
                <MenuItem value="0">All</MenuItem>
                <MenuItem value="1">Open</MenuItem>
                <MenuItem value="2">Closed</MenuItem>
              </Select>
            </div>
          </div>
          <div className="right_s">
            <Button className="btn btn_black"><FileUploadIcon /> Bulk Upload</Button>
            <Button className="btn btn_primary" onClick={() => navigate("/manage-stores/add")}><AddIcon /> Add Store</Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow hover>
                <TableCell>Store Code</TableCell>
                <TableCell>Store Name</TableCell>
                <TableCell>Address / Region</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Disable/Enable</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">No data found</TableCell>
                </TableRow>
              ) : (
                rows.map((row: RowType) => (
                  <TableRow hover key={row?.id}>
                    <TableCell>
                      <span onClick={() => navigate(`/manage-stores/detail/${row?.id}`)}>
                        <b>{row?.code || ''}</b>
                      </span>
                    </TableCell>
                    <TableCell>{row?.name || ''}</TableCell>
                    <TableCell>{row?.address || ''}, {row?.region || ''}</TableCell>
                    <TableCell>
                      <span
                        className={`status_btn dark ${row.status === "Open"
                          ? "c_success"
                          : row.status === "Closed"
                            ? "c_danger"
                            : ""
                          }`}
                      >
                        {row.status}
                      </span>
                    </TableCell>
                    <TableCell>{row?.date}</TableCell>
                    <TableCell>
                      <SwitchToggle
                        checked={Boolean(row?.raw?.isActive ?? (row?.raw?.status === 1))}
                        onChange={(checked) => handleToggle(row, checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        {isView && <IconButton onClick={() => navigate(`/manage-stores/detail/${row?.id}`)}>
                          <img src="/static/images/eye_icon.svg" alt="" />
                        </IconButton>}
                        {isEdit && <IconButton onClick={() => navigate(`/manage-stores/edit/${row?.id}`)}>
                          <img src="/static/images/edit2_icon.svg" alt="" />
                        </IconButton>}
                        {isDelete && <IconButton onClick={() => { setActiveId(row?.id); toggleDeleteModal(); }}>
                          <img src="/static/images/trash2_icon.svg" alt="" />
                        </IconButton>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          limit={limit}
          setLimit={setLimit}
          module={rows || []}
          page={page}
          setPage={setPage}
          onPageChange={onPageChange}
          totalPages={Math.max(1, Math.ceil((totalCount || 0) / (limit || 10)))}
        />
      </div>}
      <DeleteConfirmationModal
        open={openDelete}
        onClose={toggleDeleteModal}
        handleDelete={handleDelete}
        header="Are you sure you want to delete this store?"
      />
    </>
  );
};

export default ManageStores;
